import React, { useEffect, useRef, useState, useCallback } from 'react';
import { prepareWithSegments, layoutWithLines } from '@chenglou/pretext';
import ExperimentNav from '../../components/ExperimentNav';
import { experiments } from '../../App';
import './NewsTunnel.css';

const PROMPT =
  'Pretext 글자 단위 좌표 보간. 신문 컬럼 레이아웃 → 상세 아티클 레이아웃으로 각 글자가 자간/행간을 유지하며 자연스럽게 리플로우. 단일 canvas에서 모두 렌더링.';

// ── Colors ────────────────────────────────────────────────────────────────
const PAPER    = '#F5F0E8';
const INK      = '#1A1A1A';
const DARK_RED = '#8B0000';

// ── Newspaper layout ──────────────────────────────────────────────────────
const MARGIN    = 24;
const COL_GAP   = 18;
const BODY_FS   = 11;
const HEAD_FS   = 13;
const MAST_FS   = 26;
const LINE_H    = BODY_FS * 1.65;
const BODY_FONT = `${BODY_FS}px Georgia, serif`;

// ── Detail layout ─────────────────────────────────────────────────────────
const DETAIL_FS      = 15;
const DETAIL_LINE_H  = DETAIL_FS * 1.75;   // ~26.25 px — comfortable reading lead
const DETAIL_HL_BASE = 28;                  // headline font-size base

// ── Animation ─────────────────────────────────────────────────────────────
const EXPAND_DUR   = 0.95;
const COLLAPSE_DUR = 0.7;

// ── Content ───────────────────────────────────────────────────────────────
const HEADLINES = [
  'TYPOGRAPHY AS ARCHITECTURE',
  'THE TUNNEL BENEATH THE TEXT',
  'PRETEXT: SUB-PIXEL PRECISION',
  'ENTER THE RECTANGLE',
  'INK ON THE MACHINE',
  'DEPTH OF FIELD',
];
const BODY_PARAS = [
  `When text becomes structure, the page transforms into a building. Each paragraph is a room, each sentence a corridor. The reader navigates through meaning as one walks through halls. Letters stack like bricks, words form walls. Typography is not merely visual — it is spatial. We inhabit the text as much as we read it.`,
  `Beneath every surface of language lies another. Pull at a paragraph and find a passageway. The words do not end at the edge of the screen — they continue inward, folding back on themselves in right angles, forming corridors of meaning. The tunnel is not a metaphor. It is a topology.`,
  `Sub-pixel precision at sixty frames per second. Pretext measures each glyph before rendering — no layout thrashing, no DOM reflow. Characters arrive at their positions by calculation, not by accident. The newspaper grid emerges from this precision: columns that breathe, headlines that anchor.`,
  `The rectangle is the fundamental unit of digital space. The screen is a rectangle. The window is a rectangle. The paragraph is a rectangle. Click any one and find another inside — smaller, centered, identical in proportion. Then another. And another. Recursion without echo, depth without darkness.`,
  `What does it mean to print in pixels? The press was a machine that made language physical — lead type, ink, paper, pressure. The screen reverses this: pure light, no mass, no permanence. And yet the forms remain. The column. The headline. The fold. We carry the memory of ink.`,
  `Focus your gaze on the center of the tunnel. The nearest text sharpens. The farthest text blurs into texture. As the tunnel recedes, the text grows smaller, tighter, more compressed. Language becomes pattern. Pattern becomes architecture. Architecture becomes passage. Enter the passage.`,
];

// ── Types ─────────────────────────────────────────────────────────────────
interface HitRegion {
  x: number; y: number; w: number; h: number;
  colX: number; paraStartY: number; colW: number; paraIdx: number;
}

interface CharData {
  char: string;
  newsX: number; newsY: number;
  detailX: number; detailY: number;
  newsLineIdx: number; newsTotalLines: number;
}

interface DetailLayout {
  contentX: number;
  contentW: number;
  hlFontSize: number;
  hlLineH: number;
  hlLineTexts: string[];
  bodyStartY: number;
}

type Phase = 'paper' | 'expanding' | 'reading' | 'collapsing';

// ── Easing ────────────────────────────────────────────────────────────────
function easeInOutCubic(t: number): number {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// ── Measure detail layout ─────────────────────────────────────────────────
function measureDetailLayout(
  ctx: CanvasRenderingContext2D,
  paraIdx: number,
  w: number,
): DetailLayout {
  const contentX   = Math.max(32, (w - 640) / 2);
  const contentW   = Math.min(640, w - 64);
  const hlFontSize = Math.min(DETAIL_HL_BASE, Math.max(18, w * 0.034));
  const hlLineH    = hlFontSize * 1.32;
  const hlFont     = `bold ${hlFontSize}px Georgia, serif`;

  ctx.font = hlFont;
  const hlText    = HEADLINES[paraIdx % HEADLINES.length];
  const hlPrepared = prepareWithSegments(hlText, hlFont);
  const hlResult   = layoutWithLines(hlPrepared, contentW, hlLineH);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hlLineTexts: string[] = hlResult.lines.map((l: any) => l.text as string);

  const ruleY        = MARGIN + MAST_FS + 20;
  const contentStart = ruleY + 14;
  const hlH          = hlResult.lineCount * hlLineH;
  // gap (8) + rule (1.5) + gap (10)
  const bodyStartY   = contentStart + hlH + 20;

  return { contentX, contentW, hlFontSize, hlLineH, hlLineTexts, bodyStartY };
}

// ── Build newspaper char positions ────────────────────────────────────────
function buildNewsChars(
  ctx: CanvasRenderingContext2D,
  text: string,
  region: HitRegion,
): CharData[] {
  const { colX, paraStartY, colW } = region;
  ctx.font = BODY_FONT;
  const prepared   = prepareWithSegments(text, BODY_FONT);
  const result     = layoutWithLines(prepared, colW, LINE_H);
  const totalLines = result.lineCount;
  const chars: CharData[] = [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result.lines.forEach((line: any, li: number) => {
    const lineText: string = line.text;
    const lineY = paraStartY + li * LINE_H + LINE_H * 0.5;
    for (let j = 0; j < lineText.length; j++) {
      const xBefore = ctx.measureText(lineText.substring(0, j)).width;
      const xAfter  = ctx.measureText(lineText.substring(0, j + 1)).width;
      chars.push({
        char: lineText[j],
        newsX: colX + (xBefore + xAfter) / 2,
        newsY: lineY,
        detailX: 0, detailY: 0,
        newsLineIdx: li, newsTotalLines: totalLines,
      });
    }
  });
  return chars;
}

// ── Assign detail positions to char array ─────────────────────────────────
function assignDetailPositions(
  ctx: CanvasRenderingContext2D,
  chars: CharData[],
  text: string,
  layout: DetailLayout,
): void {
  const { contentX, contentW, bodyStartY } = layout;
  const detailFont = `${DETAIL_FS}px Georgia, serif`;
  ctx.font = detailFont;

  const prepared = prepareWithSegments(text, detailFont);
  const result   = layoutWithLines(prepared, contentW, DETAIL_LINE_H);

  let charIdx = 0;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  result.lines.forEach((line: any, li: number) => {
    const lineText: string = line.text;
    const lineY = bodyStartY + li * DETAIL_LINE_H + DETAIL_FS * 0.5;
    for (let j = 0; j < lineText.length; j++) {
      if (charIdx >= chars.length) return;
      const xBefore = ctx.measureText(lineText.substring(0, j)).width;
      const xAfter  = ctx.measureText(lineText.substring(0, j + 1)).width;
      chars[charIdx].detailX = contentX + (xBefore + xAfter) / 2;
      chars[charIdx].detailY = lineY;
      charIdx++;
    }
  });
}

// ── Render morphing chars ─────────────────────────────────────────────────
// expandT: 0 = newspaper positions/size, 1 = detail positions/size
function renderMorphChars(
  ctx: CanvasRenderingContext2D,
  chars: CharData[],
  progress: number,
  direction: 'expand' | 'collapse',
): void {
  const totalLines = chars[0]?.newsTotalLines || 1;
  const STAGGER = 0.18; // spread across lines

  chars.forEach(c => {
    // Earlier lines lead in expanding; later lines lead in collapsing
    const lineNorm = c.newsLineIdx / Math.max(1, totalLines - 1);
    const offset   = direction === 'expand' ? lineNorm * STAGGER : (1 - lineNorm) * STAGGER;
    const t        = Math.max(0, Math.min(1, (progress - offset) / (1 - STAGGER)));
    const eased    = easeInOutCubic(t);
    const expandT  = direction === 'expand' ? eased : 1 - eased;

    const x    = c.newsX + (c.detailX - c.newsX) * expandT;
    const y    = c.newsY + (c.detailY - c.newsY) * expandT;
    const size = BODY_FS + (DETAIL_FS - BODY_FS) * expandT;

    ctx.font = `${size.toFixed(2)}px Georgia, serif`;
    ctx.fillStyle = INK;
    ctx.textBaseline = 'middle';
    ctx.textAlign = 'center';
    ctx.fillText(c.char, x, y);
  });
  ctx.textAlign = 'left';
}

// ── Draw detail page (static, full-opacity) ───────────────────────────────
function drawDetailPage(
  ctx: CanvasRenderingContext2D,
  w: number,
  chars: CharData[],
  layout: DetailLayout,
  alpha = 1,
): void {
  const { contentX, contentW, hlFontSize, hlLineH, hlLineTexts, bodyStartY } = layout;
  ctx.globalAlpha = alpha;

  // Masthead
  ctx.fillStyle = DARK_RED;
  ctx.font = `bold ${MAST_FS}px Georgia, serif`;
  ctx.textBaseline = 'top'; ctx.textAlign = 'center';
  ctx.fillText('THE DKDK DISPATCH', w / 2, MARGIN);

  ctx.fillStyle = 'rgba(26,26,26,0.45)';
  ctx.font = '9px Georgia, serif';
  ctx.fillText('VOL. I — ISSUE 001 — SPRING 2026', w / 2, MARGIN + MAST_FS + 5);

  const ruleY = MARGIN + MAST_FS + 20;
  ctx.strokeStyle = INK; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(MARGIN, ruleY); ctx.lineTo(w - MARGIN, ruleY); ctx.stroke();
  ctx.lineWidth = 0.6;
  ctx.beginPath(); ctx.moveTo(MARGIN, ruleY + 4); ctx.lineTo(w - MARGIN, ruleY + 4); ctx.stroke();

  const contentStart = ruleY + 14;

  // Headline
  const hlFont = `bold ${hlFontSize}px Georgia, serif`;
  ctx.font = hlFont;
  ctx.fillStyle = INK;
  ctx.textBaseline = 'top'; ctx.textAlign = 'left';
  hlLineTexts.forEach((line, li) => {
    ctx.fillText(line, contentX, contentStart + li * hlLineH);
  });

  // Rule below headline
  const hlEndY = contentStart + hlLineTexts.length * hlLineH;
  ctx.strokeStyle = INK; ctx.lineWidth = 1.5;
  ctx.beginPath(); ctx.moveTo(contentX, hlEndY + 8); ctx.lineTo(contentX + contentW, hlEndY + 8); ctx.stroke();
  ctx.lineWidth = 0.5;
  ctx.beginPath(); ctx.moveTo(contentX, hlEndY + 12); ctx.lineTo(contentX + contentW, hlEndY + 12); ctx.stroke();

  // Body text at detail positions
  ctx.font = `${DETAIL_FS}px Georgia, serif`;
  ctx.fillStyle = INK;
  ctx.textBaseline = 'middle'; ctx.textAlign = 'center';
  chars.forEach(c => ctx.fillText(c.char, c.detailX, c.detailY));

  // Byline hint at bottom
  if (alpha >= 0.8) {
    ctx.font = '9px Georgia, serif';
    ctx.fillStyle = 'rgba(26,26,26,0.3)';
    ctx.textAlign = 'center'; ctx.textBaseline = 'bottom';
    ctx.fillText('press ESC or click ← to return', w / 2, bodyStartY - 8);
  }

  ctx.globalAlpha = 1;
  ctx.textAlign = 'left'; ctx.textBaseline = 'top';
}

// ── Draw newspaper ────────────────────────────────────────────────────────
function drawNewspaper(
  ctx: CanvasRenderingContext2D,
  w: number, h: number,
  hovered: number | null,
  skipParaRegionIdx: number | null,
  regions: HitRegion[],
  alpha = 1,
): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = PAPER;
  ctx.fillRect(0, 0, w, h);

  const ncols = w > 680 ? 3 : 2;
  const colW  = (w - MARGIN * 2 - COL_GAP * (ncols - 1)) / ncols;

  ctx.fillStyle = DARK_RED;
  ctx.font = `bold ${MAST_FS}px Georgia, serif`;
  ctx.textBaseline = 'top'; ctx.textAlign = 'center';
  ctx.fillText('THE DKDK DISPATCH', w / 2, MARGIN);

  ctx.fillStyle = 'rgba(26,26,26,0.5)';
  ctx.font = '9px Georgia, serif';
  ctx.fillText('VOL. I — ISSUE 001 — SPRING 2026 — DKDK.ME/FEED', w / 2, MARGIN + MAST_FS + 5);

  const ruleY = MARGIN + MAST_FS + 20;
  ctx.strokeStyle = INK; ctx.lineWidth = 2.5;
  ctx.beginPath(); ctx.moveTo(MARGIN, ruleY); ctx.lineTo(w - MARGIN, ruleY); ctx.stroke();
  ctx.lineWidth = 0.8;
  ctx.beginPath(); ctx.moveTo(MARGIN, ruleY + 5); ctx.lineTo(w - MARGIN, ruleY + 5); ctx.stroke();

  const colStartY = ruleY + 14;
  regions.length = 0;

  const parasPerCol = Math.ceil(BODY_PARAS.length / ncols);

  for (let col = 0; col < ncols; col++) {
    const colX = MARGIN + col * (colW + COL_GAP);
    let curY = colStartY;
    const startPara = col * parasPerCol;
    const endPara   = Math.min(startPara + parasPerCol, BODY_PARAS.length);

    for (let pi = startPara; pi < endPara && curY < h - MARGIN; pi++) {
      const regionIdx = regions.length;
      const isActive  = skipParaRegionIdx !== null && skipParaRegionIdx === regionIdx;
      const isDimmed  = skipParaRegionIdx !== null && !isActive;
      const paraAlpha = isDimmed ? 0.2 : 1;

      ctx.globalAlpha = alpha * paraAlpha;

      ctx.fillStyle = INK;
      ctx.font = `bold ${HEAD_FS}px Georgia, serif`;
      ctx.textBaseline = 'top'; ctx.textAlign = 'left';

      const hlWords = HEADLINES[pi % HEADLINES.length].split(' ');
      let line1 = '', line2 = '';
      for (const word of hlWords) {
        const test = line1 ? line1 + ' ' + word : word;
        if (ctx.measureText(test).width > colW) line2 += (line2 ? ' ' : '') + word;
        else line1 = test;
      }
      ctx.font = `bold ${HEAD_FS}px Georgia, serif`;
      ctx.fillText(line1, colX, curY);
      if (line2) { curY += HEAD_FS + 2; ctx.fillText(line2, colX, curY); }
      curY += HEAD_FS + 4;

      ctx.strokeStyle = INK; ctx.lineWidth = 0.5;
      ctx.beginPath(); ctx.moveTo(colX, curY); ctx.lineTo(colX + colW, curY); ctx.stroke();
      curY += 8;

      try {
        const prepared = prepareWithSegments(BODY_PARAS[pi], BODY_FONT);
        const result   = layoutWithLines(prepared, colW, LINE_H);
        const paraH    = result.lineCount * LINE_H;
        const paraStartY = curY;

        regions.push({ x: colX - 4, y: curY - 4, w: colW + 8, h: paraH + 8, colX, paraStartY, colW, paraIdx: pi });

        if (hovered === regionIdx && skipParaRegionIdx === null) {
          ctx.globalAlpha = alpha;
          ctx.fillStyle = 'rgba(139,0,0,0.06)';
          ctx.fillRect(colX - 4, curY - 4, colW + 8, paraH + 8);
          ctx.strokeStyle = 'rgba(139,0,0,0.2)';
          ctx.lineWidth = 0.5; ctx.setLineDash([4, 6]);
          ctx.strokeRect(colX - 4, curY - 4, colW + 8, paraH + 8);
          ctx.setLineDash([]);
          ctx.globalAlpha = alpha * paraAlpha;
        }

        // Skip body text of active paragraph — animated chars replace it
        if (!isActive) {
          ctx.fillStyle = INK;
          ctx.font = BODY_FONT;
          ctx.textBaseline = 'top'; ctx.textAlign = 'left';
          const startY = curY;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          result.lines.forEach((ln: any, li: number) => {
            const lineY = startY + li * LINE_H;
            if (lineY < h - MARGIN) ctx.fillText(ln.text, colX, lineY);
          });
        }

        curY += paraH + 18;
      } catch { curY += 60; }

      ctx.globalAlpha = alpha;
    }

    if (col < ncols - 1) {
      ctx.strokeStyle = skipParaRegionIdx !== null ? 'rgba(26,26,26,0.1)' : INK;
      ctx.lineWidth = 0.5; ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(colX + colW + COL_GAP / 2, colStartY);
      ctx.lineTo(colX + colW + COL_GAP / 2, h - MARGIN);
      ctx.stroke();
    }
  }
  ctx.textAlign = 'left'; ctx.globalAlpha = 1;
}

// ── Component ─────────────────────────────────────────────────────────────
const NewsTunnel: React.FC = () => {
  const canvasRef       = useRef<HTMLCanvasElement>(null);
  const animRef         = useRef<number>(0);
  const phaseRef        = useRef<Phase>('paper');
  const progressRef     = useRef(0);
  const hitRegionsRef   = useRef<HitRegion[]>([]);
  const hoveredRef      = useRef<number | null>(null);
  const activeIdxRef    = useRef<number | null>(null);
  const charDataRef     = useRef<CharData[]>([]);
  const detailLayoutRef = useRef<DetailLayout | null>(null);
  const activeTextRef   = useRef('');
  const lastTimeRef     = useRef(performance.now());

  const [phase, setPhase]       = useState<Phase>('paper');
  const [showHint, setShowHint] = useState(true);
  const exp = experiments.find(e => e.id === 'news-tunnel');

  // ── Render loop ──────────────────────────────────────────────────────────
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      canvas.width  = window.innerWidth  * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width  = `${window.innerWidth}px`;
      canvas.style.height = `${window.innerHeight}px`;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };

    const render = (now: number) => {
      animRef.current = requestAnimationFrame(render);
      const dt = Math.min((now - lastTimeRef.current) / 1000, 0.1);
      lastTimeRef.current = now;

      const w = window.innerWidth;
      const h = window.innerHeight;
      const p = phaseRef.current;

      // ── Advance ────────────────────────────────────────────────────────
      if (p === 'expanding') {
        progressRef.current = Math.min(1, progressRef.current + dt / EXPAND_DUR);
        if (progressRef.current >= 1) {
          phaseRef.current = 'reading';
          setPhase('reading');
          window.history.pushState({ newsTunnel: true }, '');
        }
      } else if (p === 'collapsing') {
        progressRef.current = Math.min(1, progressRef.current + dt / COLLAPSE_DUR);
        if (progressRef.current >= 1) {
          activeIdxRef.current    = null;
          charDataRef.current     = [];
          detailLayoutRef.current = null;
          activeTextRef.current   = '';
          phaseRef.current = 'paper';
          setPhase('paper');
        }
      }

      // ── Draw ───────────────────────────────────────────────────────────
      ctx.fillStyle = PAPER;
      ctx.fillRect(0, 0, w, h);

      if (p === 'paper') {
        drawNewspaper(ctx, w, h, hoveredRef.current, null, hitRegionsRef.current);

      } else if (p === 'expanding') {
        const prog  = progressRef.current;
        // Newspaper fades out in the first 60% of the animation
        const newsA = Math.max(0, 1 - prog / 0.6);
        if (newsA > 0) {
          drawNewspaper(ctx, w, h, null, activeIdxRef.current, hitRegionsRef.current, newsA);
        }

        // Detail chrome (masthead, headline, rule) fades in after 40%
        const detailA = Math.max(0, (prog - 0.4) / 0.6);
        if (detailA > 0 && detailLayoutRef.current) {
          // Draw everything except the body chars (those are animated)
          const layout = detailLayoutRef.current;
          ctx.globalAlpha = detailA;
          // Masthead
          ctx.fillStyle = DARK_RED;
          ctx.font = `bold ${MAST_FS}px Georgia, serif`;
          ctx.textBaseline = 'top'; ctx.textAlign = 'center';
          ctx.fillText('THE DKDK DISPATCH', w / 2, MARGIN);
          ctx.fillStyle = 'rgba(26,26,26,0.45)';
          ctx.font = '9px Georgia, serif';
          ctx.fillText('VOL. I — ISSUE 001 — SPRING 2026', w / 2, MARGIN + MAST_FS + 5);
          // Headline
          const ruleY = MARGIN + MAST_FS + 20;
          ctx.strokeStyle = INK; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.moveTo(MARGIN, ruleY); ctx.lineTo(w - MARGIN, ruleY); ctx.stroke();
          const contentStart = ruleY + 14;
          ctx.font = `bold ${layout.hlFontSize}px Georgia, serif`;
          ctx.fillStyle = INK; ctx.textBaseline = 'top'; ctx.textAlign = 'left';
          layout.hlLineTexts.forEach((line, li) => {
            ctx.fillText(line, layout.contentX, contentStart + li * layout.hlLineH);
          });
          const hlEndY = contentStart + layout.hlLineTexts.length * layout.hlLineH;
          ctx.strokeStyle = INK; ctx.lineWidth = 1.5;
          ctx.beginPath(); ctx.moveTo(layout.contentX, hlEndY + 8); ctx.lineTo(layout.contentX + layout.contentW, hlEndY + 8); ctx.stroke();
          ctx.lineWidth = 0.5;
          ctx.beginPath(); ctx.moveTo(layout.contentX, hlEndY + 12); ctx.lineTo(layout.contentX + layout.contentW, hlEndY + 12); ctx.stroke();
          ctx.globalAlpha = 1;
        }

        // Animated chars
        if (charDataRef.current.length > 0) {
          renderMorphChars(ctx, charDataRef.current, prog, 'expand');
        }

      } else if (p === 'reading') {
        if (detailLayoutRef.current && charDataRef.current.length > 0) {
          drawDetailPage(ctx, w, charDataRef.current, detailLayoutRef.current);
        }

      } else if (p === 'collapsing') {
        const prog = progressRef.current;
        // Detail chrome fades out in first 50%
        const detailA = Math.max(0, 1 - prog / 0.5);
        if (detailA > 0 && detailLayoutRef.current) {
          drawDetailPage(ctx, w, charDataRef.current, detailLayoutRef.current, detailA);
        }
        // Newspaper fades in after 40%
        const newsA = Math.max(0, (prog - 0.4) / 0.6);
        if (newsA > 0) {
          drawNewspaper(ctx, w, h, null, activeIdxRef.current, hitRegionsRef.current, newsA);
        }
        // Animated chars
        if (charDataRef.current.length > 0) {
          renderMorphChars(ctx, charDataRef.current, prog, 'collapse');
        }
      }
    };

    window.addEventListener('resize', resize);
    document.fonts.ready.then(() => {
      resize();
      animRef.current = requestAnimationFrame(render);
    });

    return () => {
      cancelAnimationFrame(animRef.current);
      window.removeEventListener('resize', resize);
    };
  }, []);

  // ── handleBack ────────────────────────────────────────────────────────────
  const handleBack = useCallback(() => {
    if (phaseRef.current !== 'reading') return;
    progressRef.current  = 0;
    phaseRef.current     = 'collapsing';
    setPhase('collapsing');
  }, []);

  // ── Browser back + ESC ────────────────────────────────────────────────────
  useEffect(() => {
    const onPop = () => { if (phaseRef.current === 'reading') handleBack(); };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [handleBack]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') handleBack(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [handleBack]);

  // ── Mouse ─────────────────────────────────────────────────────────────────
  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (phaseRef.current !== 'paper') return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const { left, top } = canvas.getBoundingClientRect();
    const mx = e.clientX - left, my = e.clientY - top;
    let found: number | null = null;
    for (let i = 0; i < hitRegionsRef.current.length; i++) {
      const r = hitRegionsRef.current[i];
      if (mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h) { found = i; break; }
    }
    hoveredRef.current = found;
    canvas.style.cursor = found !== null ? 'pointer' : 'default';
  }, []);

  const handleClick = useCallback((e: React.MouseEvent) => {
    const p = phaseRef.current;
    // Allow back-click on reading phase (click anywhere returns)
    if (p === 'reading') { handleBack(); return; }
    if (p !== 'paper') return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const { left, top } = canvas.getBoundingClientRect();
    const mx = e.clientX - left, my = e.clientY - top;
    let found: number | null = null;
    for (let i = 0; i < hitRegionsRef.current.length; i++) {
      const r = hitRegionsRef.current[i];
      if (mx >= r.x && mx <= r.x + r.w && my >= r.y && my <= r.y + r.h) { found = i; break; }
    }
    if (found === null) return;

    const region  = hitRegionsRef.current[found];
    const text    = BODY_PARAS[region.paraIdx];
    const layout  = measureDetailLayout(ctx, region.paraIdx, window.innerWidth);

    const chars = buildNewsChars(ctx, text, region);
    assignDetailPositions(ctx, chars, text, layout);

    charDataRef.current     = chars;
    detailLayoutRef.current = layout;
    activeTextRef.current   = text;
    activeIdxRef.current    = found;
    progressRef.current     = 0;
    setShowHint(false);
    phaseRef.current = 'expanding';
    setPhase('expanding');
  }, [handleBack]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="newsTunnel">
      <canvas ref={canvasRef} onMouseMove={handleMouseMove} onClick={handleClick} />
      <ExperimentNav prompt={exp?.prompt || PROMPT} title="News Tunnel" />
      {showHint && phase === 'paper' && (
        <div className="ntHint">click any paragraph to read</div>
      )}
      {phase === 'reading' && (
        <button className="ntDetailBack" onClick={handleBack}>← back to paper</button>
      )}
    </div>
  );
};

export default NewsTunnel;
